import { ReactElement, ReactNode, useContext, useState, useEffect } from 'react';
import MenuBar from './menuBar/MenuBar';
import Header from './header/Header';
import LayoutStore from '../../store/LayoutStore';
import FavoritosStore from '../../store/FavoritosStore';
import NavegacionStore from '../../store/NavegacionStore';
import ContentContainer from './ContentContainer';
import LayoutContext from '../../contexts/LayoutContext';
import classNames from 'classnames';

import TwoFactorReminderModal from '../TwoFactorReminderModal';
import AuthContext from '../../contexts/AuthContext';
import { useHistory } from 'react-router-dom';
import SessionTimeoutModal from '../common/SessionTimeoutModal';
import { getUser } from '../../api/UsersApi';
import Enable2FAModal from '../Enable2FAModal';
import { enable2FA } from '../../api/LoginApi';
import cogoToast from 'cogo-toast';
import AgenteMTeamChat from '../agenteMTeam/AgenteMTeamChat';
interface LayoutProps {
    children?: ReactNode;
}

const LayoutContent = ({ children }: LayoutProps): ReactElement => {
    const { isMenuBarActive } = useContext(LayoutContext);
    const { userEmail, userRut, userRoles } = useContext(AuthContext);
    const history = useHistory();
    // El rol CLIENTE no tiene menú: sin barra de íconos, el contenido ocupa todo el ancho.
    const conRail = !userRoles.includes('CLIENTE');
    const wrapperClassNames = classNames('app-wrapper', { 'menu-open': isMenuBarActive && conRail, 'con-rail': conRail });

    const [showReminderModal, setShowReminderModal] = useState(false);
    const [has2FAEnabled, setHas2FAEnabled] = useState<boolean | null>(null);
    const [showEnable2FAModal, setShowEnable2FAModal] = useState(false);

    useEffect(() => {
        const check2FAStatus = async () => {
            try {
                if (userRut) {
                    const response = await getUser(userRut);
                    setHas2FAEnabled(response.data.twoFactorEnabled || false);
                }
            } catch (error) {
                console.error('[2FA REMINDER] Error checking 2FA status:', error);
            }
        };

        if (userEmail && userRut) {
            check2FAStatus();
        }
    }, [userEmail, userRut]);

    useEffect(() => {
        const isAdmin = userRut === 'adminERPElectrans';
        if (has2FAEnabled === false && !isAdmin) {
            const checkAndShowModal = () => {
                const lastDismissed = localStorage.getItem(`2fa_reminder_dismissed_${userEmail}`);
                const cooldownTime = 24 * 60 * 60 * 1000; // 24 hours
                const now = new Date().getTime();

                if (!lastDismissed || (now - parseInt(lastDismissed, 10)) > cooldownTime) {
                    setShowReminderModal(true);
                }
            };

            checkAndShowModal();

            const interval = setInterval(() => {
                checkAndShowModal();
            }, 2000000);

            return () => {
                clearInterval(interval);
            };
        }
    }, [has2FAEnabled, userEmail]);

    const handleActivate2FA = () => {
        setShowReminderModal(false);
        setShowEnable2FAModal(true);
    };

    const handleEnable2FAModalSubmit = async (method: 'EMAIL' | 'TOTP', interval: number, code?: string) => {
        try {
            await enable2FA(userEmail, method, interval, code);
            setShowEnable2FAModal(false);
            setHas2FAEnabled(true);
            cogoToast.success('2FA activado exitosamente');
        } catch (error: any) {
            const msg = error.response?.data?.message || 'Error al activar 2FA';
            cogoToast.error(msg);
        }
    };

    const handleSetupTOTP = () => {
        setShowEnable2FAModal(false);
        history.push({
            pathname: '/setup-2fa',
            state: { email: userEmail }
        });
    };

    const handleDismiss = () => {
        const now = new Date().getTime();
        localStorage.setItem(`2fa_reminder_dismissed_${userEmail}`, now.toString());
        setShowReminderModal(false);
    };

    return (
        <>
            <MenuBar />
            <div className={wrapperClassNames}>
                <Header />
                <ContentContainer>{children}</ContentContainer>

                <TwoFactorReminderModal
                    show={showReminderModal}
                    onActivate={handleActivate2FA}
                    onDismiss={handleDismiss}
                />
                <Enable2FAModal
                    show={showEnable2FAModal}
                    userEmail={userEmail}
                    onEnable={handleEnable2FAModalSubmit}
                    onSkip={() => setShowEnable2FAModal(false)}
                    onSetupTOTP={handleSetupTOTP}
                />
                <SessionTimeoutModal />
            </div>
            <AgenteMTeamChat />
        </>
    );
};

const Layout = ({ children }: LayoutProps): ReactElement => {
    return (
        <LayoutStore>
            <FavoritosStore>
                <NavegacionStore>
                    <LayoutContent>{children}</LayoutContent>
                </NavegacionStore>
            </FavoritosStore>
        </LayoutStore>
    );
};

export default Layout;